import React, { useEffect, useState } from "react";
import axios from "axios";
import logo from "../assets/images/logo_2.png";
import { Link, useNavigate } from "react-router-dom";
import { FiLogOut } from "react-icons/fi";
const BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function AlertsPage() {
  const [clients, setClients] = useState([]);
  const [marginInputs, setMarginInputs] = useState({});
  const [telegramPopupId, setTelegramPopupId] = useState(null);
  const [telegramInput, setTelegramInput] = useState("");
  const [darkMode, setDarkMode] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${BASE_URL}/api/login-alerts`);
      setClients(res.data.data || []);
    } catch (err) {
      console.error("Failed to fetch alerts data:", err);
      setError("Failed to load client data. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Toggle alert_active per client
  const toggleAlertActive = async (sn, val) => {
    try {
      const response = await axios.patch(
        `${BASE_URL}/api/login-alerts/${sn}/active`,
        { alert_active: val ? 1 : 0 },
        { headers: { 'Content-Type': 'application/json' } }
      );
      if (response.data.success) {
        setClients((prev) =>
          prev.map((client) =>
            client.SN === sn ? { ...client, alert_active: val ? 1 : 0 } : client
          )
        );
      } else {
        throw new Error(response.data.message || "Toggle failed");
      }
    } catch (err) {
      console.error("Toggle error:", err);
      setError(
        err.response?.data?.message || "Failed to toggle alerts. Please try again."
      );
      fetchClients();
    }
  };

  const handleMarginLevelChange = (sn, value) => {
    setMarginInputs((prev) => ({ ...prev, [sn]: value }));
  };

  const saveMarginLevel = async (sn) => {
    const value = parseFloat(marginInputs[sn]);
    if (isNaN(value)) {
      setError("Please enter a valid margin percentage");
      return;
    }
    try {
      await axios.patch(
        `${BASE_URL}/api/login-alerts/${sn}/margin`,
        { alert_margin_level: value }
      );
      fetchClients();
    } catch (err) {
      console.error("Save margin error:", err);
      setError("Error saving margin level. Please try again.");
    }
  };

  const openTelegramPopup = (sn, current) => {
    setTelegramPopupId(sn);
    setTelegramInput(current || "");
  };

  const saveTelegramId = async () => {
    if (!telegramInput.trim()) {
      setError("Please enter a valid Telegram ID");
      return;
    }
    try {
      await axios.patch(
        `${BASE_URL}/api/login-alerts/${telegramPopupId}/telegram`,
        { telegram_id: telegramInput }
      );
      setTelegramPopupId(null);
      setTelegramInput("");
      fetchClients();
    } catch (err) {
      console.error("Save Telegram error:", err);
      setError("Failed to save Telegram ID. Please try again.");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  const formatMT4Accounts = (accounts) => {
    if (!accounts) return "";
    return accounts
      .replace(/[\[\]\{\}"]/g, "")
      .split(",")
      .map((a) => a.trim())
      .filter(Boolean)
      .join(", ");
  };

  return (
    <div
      className={`${
        darkMode ? "bg-gray-900 text-gray-100" : "bg-white text-gray-900"
      } min-h-screen`}
    >
      {/* Navbar */}
      <nav
        className={`w-full px-4 py-3 flex flex-col sm:flex-row justify-between items-center sticky top-0 z-50 shadow-md ${
          darkMode ? "bg-gray-800" : "bg-gray-100"
        }`}
      >
        <div className="flex items-center gap-3 mb-3 sm:mb-0">
          <img src={logo} className="h-8" alt="Logo" />
          <h1
            className={`text-xl font-bold ${
              darkMode ? "text-white" : "text-black"
            }`}
          >
            Account Dashboard
          </h1>
        </div>

        <div className="flex items-center gap-2 flex-wrap justify-center w-full sm:w-auto">
          {["Dashboard", "AddClient", "Viewclients", "AlertsSettings"].map(
            (route, i) => (
              <Link key={i} to={`/${route}`}>
                <button
                  className={`px-2 py-1 rounded text-xs ${
                    darkMode
                      ? "hover:bg-gray-700"
                      : "hover:bg-gray-300"
                  } ${
                    route === "AlertsSettings"
                      ? darkMode
                        ? "bg-blue-500 hover:bg-blue-600"
                        : "bg-blue-500 hover:bg-blue-600 text-white"
                      : ""
                  }`}
                >
                  {route.replace(/([A-Z])/g, " $1").trim()}
                </button>
              </Link>
            )
          )}
        </div>

        <div className="flex items-center gap-2 mt-3 sm:mt-0">
          <label className="inline-flex relative items-center cursor-pointer">
            <input
              type="checkbox"
              checked={darkMode}
              onChange={() => setDarkMode(!darkMode)}
              className="sr-only peer"
            />
            <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
          <button
            onClick={handleLogout}
            className="bg-red-600 px-3 py-1 rounded text-xs flex items-center gap-1 text-white"
          >
            <FiLogOut size={14} /> Logout
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <div className="p-3">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
          <h2 className="text-xl font-bold">Client Alert Settings</h2>
          <div className="flex items-center gap-3">
            {loading && (
              <span className="text-xs text-blue-500">Loading...</span>
            )}
            <span
              className={`text-xs ${
                darkMode ? "text-gray-400" : "text-gray-600"
              }`}
            >
              {clients.length} {clients.length === 1 ? "Client" : "Clients"}
            </span>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div
            className={`mb-3 p-2 rounded text-sm ${
              darkMode
                ? "bg-red-900 text-red-100"
                : "bg-red-100 text-red-900"
            }`}
          >
            {error}
            <button
              onClick={() => setError(null)}
              className="float-right font-bold ml-2"
            >
              ×
            </button>
          </div>
        )}

        {/* Table */}
        <div className="w-full overflow-x-auto rounded-lg border shadow-sm">
          <table className={`w-full ${darkMode ? "bg-gray-800" : "bg-white"}`}>
            <thead
              className={
                darkMode ? "bg-gray-700 text-white" : "bg-gray-200 text-gray-800"
              }
            >
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium uppercase">
                  #
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium uppercase">
                  Name
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium uppercase">
                  MT Accounts
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium uppercase">
                  Email
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium uppercase">
                  Telegram
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium uppercase">
                  Margin %
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium uppercase">
                  Alerts
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {clients.map((client, i) => (
                <tr
                  key={client.SN}
                  className={
                    darkMode ? "hover:bg-gray-700" : "hover:bg-gray-50"
                  }
                >
                  <td className="px-3 py-2 whitespace-nowrap text-sm">{i + 1}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm font-medium">
                    {client.Name}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm max-w-[120px] overflow-hidden text-ellipsis">
                    {formatMT4Accounts(client.MT4_Account)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm">
                    {client.Username}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <button
                      className={`px-2 py-1 rounded text-xs ${
                        darkMode
                          ? "bg-blue-600 hover:bg-blue-700"
                          : "bg-blue-500 hover:bg-blue-600"
                      } text-white`}
                      onClick={() =>
                        openTelegramPopup(client.SN, client.telegram_id)
                      }
                    >
                      {client.telegram_id ? "Edit" : "Add"}
                    </button>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <input
                        type="number"
                        className={`w-14 px-2 py-1 rounded border text-xs ${
                          darkMode
                            ? "bg-gray-700 border-gray-600 text-white"
                            : "bg-white border-gray-300 text-black"
                        }`}
                        placeholder="%"
                        value={
                          marginInputs[client.SN] ?? client.alert_margin_level ?? ""
                        }
                        onChange={(e) =>
                          handleMarginLevelChange(client.SN, e.target.value)
                        }
                      />
                      <button
                        className={`px-2 py-1 rounded text-xs ${
                          darkMode
                            ? "bg-green-600 hover:bg-green-700"
                            : "bg-green-500 hover:bg-green-600"
                        } text-white`}
                        onClick={() => saveMarginLevel(client.SN)}
                      >
                        Save
                      </button>
                    </div>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={client.alert_active === 1}
                        onChange={e =>
                          toggleAlertActive(client.SN, e.target.checked)
                        }
                        className="sr-only peer"
                      />
                      <div className="w-9 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-green-500"></div>
                    </label>
                  </td>
                </tr>
              ))}
              {!loading && !clients.length && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-3 py-4 text-center text-sm text-gray-500"
                  >
                    No clients found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Telegram Modal */}
      {telegramPopupId && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div
            className={`p-4 rounded-lg shadow-xl w-full max-w-md ${
              darkMode ? "bg-gray-800" : "bg-white"
            }`}
          >
            <h3
              className={`text-lg font-bold mb-3 ${
                darkMode ? "text-white" : "text-black"
              }`}
            >
              {telegramInput ? "Update" : "Add"} Telegram ID
            </h3>
            <input
              type="text"
              value={telegramInput}
              onChange={(e) => setTelegramInput(e.target.value)}
              className={`w-full mb-3 p-2 rounded border text-sm ${
                darkMode
                  ? "bg-gray-700 border-gray-600 text-white"
                  : "bg-white border-gray-300 text-black"
              }`}
              placeholder="Enter Telegram ID"
            />
            <div className="flex justify-end gap-2">
              <button
                className={`px-3 py-1 rounded text-sm ${
                  darkMode
                    ? "bg-gray-600 hover:bg-gray-500"
                    : "bg-gray-300 hover:bg-gray-400"
                }`}
                onClick={() => setTelegramPopupId(null)}
              >
                Cancel
              </button>
              <button
                className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                onClick={saveTelegramId}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
